import { action, computed, observable, runInAction } from "mobx";
import { DateTime } from "luxon";

import { LoaderStore } from "@state/stores/common";
import {
	SharedBetSlipThumbnail,
	TSharedBetSlipResponse,
} from "@api-types/quick-play/SharedBetSlipDto";
import BetDetailsStore from "@state/stores/my-bets/BetDetailsStore";
import { Bet } from "@models";

import QuickPlayStore from "./QuickPlayStore";
import { ChangePageButton } from "./MostPlayedTicketsStore";
import { SharedBetSlipsApiService } from "@api-services/shared-bet-slips";
import { BetSlipShareDetails } from "@state/stores/my-bets/BetSlipShareDetailsStore";
import { BetSlipShare } from "@models/BetSlipShare";

export default class SharedBetSlipStore {
	@observable quickPlayStore: QuickPlayStore;
	@observable betSlipShareDetails: BetSlipShareDetails;
	@observable loader: LoaderStore;

	@observable sharedBetSlips: SharedBetSlipThumbnail[] = [];
	@observable sharedBetSlipPopup: BetSlipShare | null;

	@observable indexOfBetslip: number = 0;
	@observable isStoreInitialized: boolean = false;
	@observable isPopupDisplayed: boolean = false;
	@observable blockMainLoader: boolean = false;
	@observable showLoader: ChangePageButton | null = null;

	constructor(quickPlayStore: QuickPlayStore) {
		this.loader = new LoaderStore();
		this.quickPlayStore = quickPlayStore;
	}

	@computed get shouldShowLoader() {
		return this.loader.isLoading;
	}

	@computed get shouldShowEmptyState() {
		return this.sharedBetSlips.length == 0 && !this.loader.isLoadingProcess;
	}

	private initializeBetDetailsStore() {
		this.betSlipShareDetails = new BetSlipShareDetails(
			this.quickPlayStore.rootStore,
			0
		);
	}

	@action.bound
	async changePage(num: ChangePageButton) {
		this.showLoader = num;
		if (!this.blockMainLoader) this.blockMainLoader = true;

		switch (num) {
			case ChangePageButton.PREVIOUS:
				if (this.indexOfBetslip > 0) {
					this.indexOfBetslip -= 1;
				} else {
					this.indexOfBetslip = this.sharedBetSlips.length - 1;
				}
				break;
			case ChangePageButton.NEXT:
				if (this.indexOfBetslip < this.sharedBetSlips.length - 1) {
					this.indexOfBetslip += 1;
				} else {
					this.indexOfBetslip = 0;
				}
				break;
			default:
				this.indexOfBetslip = num;
				break;
		}
		await this.changeBetSlip();
	}

	@action.bound
	async fetchBetSlipDetails(betSlipNumber: string) {
		await this.betSlipShareDetails.getSharedBetSlipDetails(betSlipNumber);

		runInAction(() => {
			if (this.betSlipShareDetails.bet != null) {
				this.sharedBetSlipPopup = this.betSlipShareDetails.bet;
			} else {
				this.deactivatePopup();
			}
		});
	}

	@action.bound
	async changeBetSlip() {
		const betSlipNumber =
			this.sharedBetSlips[this.indexOfBetslip].betSlipNumber;

		this.fetchBetSlipDetails(betSlipNumber);
	}

	@action.bound
	public async onInitialize() {
		this.isStoreInitialized = true;
		await this.fetchSharedBetSlips();
		this.initializeBetDetailsStore();
	}

	@action.bound
	async activatePopup(betSlipNumber: string, index: number) {
		this.indexOfBetslip = index;
		this.isPopupDisplayed = true;

		this.fetchBetSlipDetails(betSlipNumber);
	}

	setBetSlipThumbnail(
		sharedBetSlip: TSharedBetSlipResponse
	): SharedBetSlipThumbnail {
		let eventName: string;

		const firstOfferElement = sharedBetSlip.betSlipOfferInfo[0];

		const firstElementSportAbrv: string = firstOfferElement.sportAbrv;

		const isMultiSport = !sharedBetSlip.betSlipOfferInfo.every(
			(x) => firstElementSportAbrv === x.sportAbrv
		);

		eventName = firstOfferElement.eventName
			? firstOfferElement.eventName
			: [
					firstOfferElement.teamOneName,
					firstOfferElement.teamTwoName,
				].join(" - ");

		return {
			id: sharedBetSlip.id,
			eventCount: sharedBetSlip.betSlipInfo.numberOfBets,
			maxCoefficient: sharedBetSlip.betSlipInfo.maxCoefficient,
			betSlipNumber: sharedBetSlip.betSlipInfo.betSlipNumber,
			dateCreated: DateTime.fromISO(
				sharedBetSlip.dateCreated
			).toLocaleString(DateTime.DATETIME_SHORT),
			sportAbrv: isMultiSport ? "multi" : firstElementSportAbrv,
			firstEventName: eventName,
			betStatus: sharedBetSlip.betSlipInfo.betStatus,
			chatName: sharedBetSlip.betSlipInfo.chatName || "-",
			isMultiOffer:
				sharedBetSlip.betSlipInfo.betSlipType.abrv ==
				"multi-offer-bet-slip",
			betSlipDateCreated: sharedBetSlip.betSlipInfo.dateCreated,
		};
	}

	@action.bound
	deactivatePopup() {
		this.blockMainLoader = false;
		this.indexOfBetslip = 0;
		this.sharedBetSlipPopup = null;
		this.isPopupDisplayed = false;
	}

	@action.bound
	private async fetchSharedBetSlips() {
		this.loader.suspend();
		try {
			const response =
				await SharedBetSlipsApiService.fetchAllSharedBetSlips();
			runInAction(() => {
				this.sharedBetSlips = response.item.map((betSlip) =>
					this.setBetSlipThumbnail(betSlip)
				);
			});
		} catch (e) {
			this.sharedBetSlips = [];
			console.error(e);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	onDispose() {
		this.sharedBetSlips = [];
		this.showLoader = null;
		this.blockMainLoader = false;
		this.sharedBetSlipPopup = null;
		this.isStoreInitialized = false;
		this.isPopupDisplayed = false;
	}
}
